import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { take } from 'rxjs/operators';
import { ApsPermitedScreen } from 'src/app/models/aps-permission/aps-permited-screen';
import { ConstantsFordProtect } from 'src/app/models/constants/constants-ford-protect';
import { LanguageApplication } from 'src/app/models/interface/common/language.application';
import { ApsPermissionService } from 'src/app/services/aps-permission/aps-permission.service';
import { I18nBrokerService } from 'src/app/services/i18n/i18n-broker.service';

@Component({
	selector: 'app-header',
	templateUrl: './header.component.html',
	standalone: false
})
export class HeaderComponent implements OnInit, OnDestroy {

	allPermissions: string[] = [];
	isAccessManagement = false;
	languageActive = false;
	languages: LanguageApplication[] = [];
	selectedLanguage: string | null = null;
	systemCountries: LanguageApplication[] = [];
	selectedSystemCountry: string | null = null;
	snapshotUrl = '';
	userCountry: string = '';
	getSystemCountrySelected = '';
	countryCheckInterval: ReturnType<typeof setInterval> | null = null;
	attemptCount = 0;
	readonly MAX_ATTEMPTS = 25;

	constructor(
		private apsPermissionService: ApsPermissionService,
		private i18nBroker: I18nBrokerService,
		private router: Router,
	) {

		this.userCountry = apsPermissionService.getUserCountry();

		console.log("País do usuário: ", this.userCountry);

		let defaultLang: string = this.i18nBroker.resolveLanguageByCountry(this.userCountry);
		if (!this.i18nBroker.isSupportedLanguage(defaultLang)) {
			defaultLang = ConstantsFordProtect.PT_BR;
		}

		this.selectedLanguage = defaultLang;
		this.changeLang(defaultLang);

		this.countrySelect();

		this.systemCountries = [
			{ label: 'TRANSLATE.CHOOSE.COUNTRY.ARG', value: ConstantsFordProtect.ARG, flag: ConstantsFordProtect.ES_AR },
			{ label: 'TRANSLATE.CHOOSE.COUNTRY.BRA', value: ConstantsFordProtect.BRA, flag: ConstantsFordProtect.PT_BR },
			{ label: 'TRANSLATE.CHOOSE.COUNTRY.CHL', value: ConstantsFordProtect.CHL, flag: ConstantsFordProtect.ES_CL },
			{ label: 'TRANSLATE.CHOOSE.COUNTRY.COL', value: ConstantsFordProtect.COL, flag: ConstantsFordProtect.ES_CO },
			{ label: 'TRANSLATE.CHOOSE.COUNTRY.VEN', value: ConstantsFordProtect.VEN, flag: ConstantsFordProtect.ES_VE }
		];
	}

	ngOnInit(): void {

		console.log('Iniciando o HeaderComponent...');

		void (async () => {
			// recuperar a url
			this.snapshotUrl = this.router.url;

			// verifica se a url é aberta. Se sim, omite os itens de menu
			if (!this.snapshotUrl.includes(ConstantsFordProtect.OPEN_URL_FRONT)) {
				await this.apsPermissionService.bootstrapPermissions();
				this.getPermissions();
			}

			this.attemptCount = 0;
			this.countryCheckInterval = setInterval(() => {

				this.attemptCount++;
				this.userCountry = this.apsPermissionService.getUserCountry();

				if (this.userCountry !== '') {

					this.clearCountryCheckInterval();
					sessionStorage.setItem(ConstantsFordProtect.SYSTEM_COUNTRY, this.userCountry);
					this.countrySelect();
					console.log(`País obtido após ${this.attemptCount} tentativas.`);

				} else if (this.attemptCount >= this.MAX_ATTEMPTS) {

					this.clearCountryCheckInterval();
					console.warn(`Número máximo de tentativas (${this.MAX_ATTEMPTS}) excedido para obter o país do usuário!`);
					console.warn('Prosseguindo sem o país ou com um valor padrão');

				} else {

					console.log('Aguardando o país do usuário por mais 200 milisegundos...');
					console.log(`Tentativa: ${this.attemptCount}`);
				}
			}, 200);

		})();

	}

	private clearCountryCheckInterval(): void {
		if (this.countryCheckInterval !== null) {
			clearInterval(this.countryCheckInterval);
			this.countryCheckInterval = null;
		}
	}

	changeLang(lang: string | null) {

		if (!lang) {
			return;
		}

		this.i18nBroker.changeLanguage(lang)
			.pipe(take(1))
			.subscribe((resolvedLanguage) => {
				this.selectedLanguage = resolvedLanguage;
				this.languageActive = true;
			});
	}

	changeSystemCountry(systemCountrySelected: string | null) {

		if (!systemCountrySelected) {
			return;
		}

		sessionStorage.removeItem(ConstantsFordProtect.SYSTEM_COUNTRY);
		sessionStorage.setItem(ConstantsFordProtect.SYSTEM_COUNTRY, systemCountrySelected);
		this.getSystemCountrySelected = systemCountrySelected;
		this.selectedSystemCountry = systemCountrySelected;

		const selectedLangStandard = this.i18nBroker.resolveLanguageByCountry(systemCountrySelected);

		// Atualiza o select de idiomas e seleciona o idioma padrão
		this.countrySelect();
		this.selectedLanguage = selectedLangStandard;
		this.changeLang(selectedLangStandard);

		this.router.navigate(['home'], { skipLocationChange: false, replaceUrl: false });
	}

	countrySelect() {

		this.languages = [
			{ label: 'TRANSLATE.CHOOSE.COUNTRY.LANGUAGE.ARG', value: ConstantsFordProtect.ES_AR, flag: ConstantsFordProtect.ES_AR },
			{ label: 'TRANSLATE.CHOOSE.COUNTRY.LANGUAGE.BRA', value: ConstantsFordProtect.PT_BR, flag: ConstantsFordProtect.PT_BR },
			{ label: 'TRANSLATE.CHOOSE.COUNTRY.LANGUAGE.CHL', value: ConstantsFordProtect.ES_CL, flag: ConstantsFordProtect.ES_CL },
			{ label: 'TRANSLATE.CHOOSE.COUNTRY.LANGUAGE.COL', value: ConstantsFordProtect.ES_CO, flag: ConstantsFordProtect.ES_CO },
			{ label: 'TRANSLATE.CHOOSE.COUNTRY.LANGUAGE.VEN', value: ConstantsFordProtect.ES_VE, flag: ConstantsFordProtect.ES_VE }
		];

		if (!this.selectedLanguage || !this.languages.some(lang => lang.value === this.selectedLanguage)) {
			this.selectedLanguage = null;
		}

	}

	async getPermissions() {

		await this.getListAllPermissions().then((success: string[]) => {
			this.allPermissions = success;
		});

		await this.loadScreenPermissions();
	}

	async getListAllPermissions(): Promise<string[]> {

		let allPermissions: string[] = [];
		await this.apsPermissionService.getListPermissions().then((list: string[]) => {
			allPermissions = list;
		});
		return allPermissions;
	}

	async loadScreenPermissions() {

		await this.checkPermission(ApsPermitedScreen.accessManagement).then((success: boolean) => {
			if (success) {
				this.isAccessManagement = true;
			}
		});
	}

	async checkPermission(permissionName: string): Promise<boolean> {

		let checkPermission = false;
		this.allPermissions.forEach(pm => {
			if (pm === permissionName) {
				checkPermission = true;
			}
		});

		return checkPermission;
	}

	ngOnDestroy(): void {
		this.clearCountryCheckInterval();
	}

	navigateToHome(): void {
		void this.router.navigate(['home'], { skipLocationChange: false, replaceUrl: false });
	}

}